import { requestRefresh, getRefreshEpoch } from 'dashboard/helper/personalReadRefresh';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import ConversationApi from '../../../../api/inbox/conversation';
import mutationTypes from '../../../mutation-types';
import {
  loadedMessageIds,
  isPersonalReadViewActive,
} from 'dashboard/helper/personalRead';

const pending = new Map();
const serialize = (key, operation) => {
  const next = (pending.get(key) || Promise.resolve())
    .catch(() => {})
    .then(operation);
  pending.set(key, next);
  return next.finally(() => {
    if (pending.get(key) === next) pending.delete(key);
  });
};
const chatFor = (state, id) =>
  state.allConversations.find((chat) => Number(chat.id) === Number(id));
const userContext = (rootGetters) => [
  rootGetters?.getCurrentAccountId,
  rootGetters?.getCurrentUserID,
  getRefreshEpoch(),
];
const contextMatches = (rootGetters, identity) =>
  userContext(rootGetters).every((value, index) => value === identity[index]);
const apply = (commit, data, identity) => {
  if (
    data.personal_read_enabled &&
    data.personal_read_user_id === identity[1]
  ) {
    commit('APPLY_PERSONAL_READ', data);
  } else if (!data.personal_read_enabled) {
    commit(mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT, {
      id: data.id,
      lastSeen: data.agent_last_seen_at,
      unreadCount: data.unread_count,
    });
  }
};

export default {
  refreshPersonalReadState({ state, commit, rootGetters }, { id }) {
    const identity = userContext(rootGetters);
    return requestRefresh(`${identity.join(':')}:${id}`, async () => {
      const ids = loadedMessageIds(chatFor(state, id)?.messages);
      for (let offset = 0; offset < Math.max(ids.length, 1); offset += 200) {
        if (!contextMatches(rootGetters, identity)) return;
        const { data } = await ConversationApi.getPersonalRead({
          id,
          messageIds: ids.slice(offset, offset + 200),
        });
        if (!contextMatches(rootGetters, identity)) return;
        apply(commit, data, identity);
      }
    }, serialize).catch(() => {});
  },

  acknowledgeRenderedMessages(
    { state, commit, rootGetters, dispatch },
    { id, messageIds, generation },
  ) {
    const identity = userContext(rootGetters);
    let acknowledged = false;
    const active = () =>
      contextMatches(rootGetters, identity) &&
      isPersonalReadViewActive({
        conversationId: id,
        selectedId: state.selectedChatId,
        accountId: identity[0],
        currentAccountId: rootGetters.getCurrentAccountId,
      });
    return serialize(`${identity.join(':')}:${id}`, async () => {
      if (!active()) return;
      const currentlyLoaded = new Set(
        loadedMessageIds(chatFor(state, id)?.messages),
      );
      const ids = [...new Set(messageIds)].filter((messageId) =>
        currentlyLoaded.has(messageId),
      );
      let observedGeneration = generation;
      let remainingBatches = 100;
      for (let offset = 0; offset < ids.length; offset += 200) {
        const observedIds = ids.slice(offset, offset + 200);
        let previousBatchIds;
        // Keep the same displayed boundary throughout continuation. The
        // server signs the eligible history, never a new client-side horizon.
        while (remainingBatches > 0) {
          if (!active()) return;
          remainingBatches -= 1;
          const { data } = await ConversationApi.getPersonalRead({
            id,
            messageIds: observedIds,
            readThrough: true,
          });
          if (!contextMatches(rootGetters, identity)) return;
          apply(commit, data, identity);
          if (!active() || !data.personal_read_enabled) return;
          if (
            observedGeneration !== undefined &&
            observedGeneration !== data.personal_read_generation
          )
            return;
          observedGeneration = data.personal_read_generation;
          const batchIds = (data.personal_read_batch_ids || []).join(',');
          if (!data.personal_read_token || !batchIds) break;
          if (batchIds === previousBatchIds) return;
          previousBatchIds = batchIds;
          const { data: result } = await ConversationApi.markMessageRead({
            id,
            readToken: data.personal_read_token,
          });
          if (!contextMatches(rootGetters, identity)) return;
          apply(commit, result, identity);
          if (result.personal_read_generation !== observedGeneration) return;
          acknowledged = true;
          if (!data.personal_read_more) break;
        }
        if (remainingBatches === 0) return;
      }
    })
      .catch(() => {})
      .finally(() => {
        // Update this window without waiting for the background WebSocket event;
        // the private event remains responsible for the reader's other windows.
        if (acknowledged && contextMatches(rootGetters, identity)) {
          dispatch('conversationUnreadCounts/get', {}, { root: true });
        }
      });
  },

  async markMessagesRead(
    { state, commit, rootGetters, dispatch },
    { id, explicit = false },
  ) {
    const identity = userContext(rootGetters);
    const chat = state && chatFor(state, id);
    if (chat?.personal_read_enabled && !explicit) return;
    if (explicit) {
      return serialize(`${identity.join(':')}:${id}`, async () => {
        let throughId;
        let previousIds;
        let generation;
        for (let page = 0; page < 100; page += 1) {
          if (!contextMatches(rootGetters, identity))
            throw new Error('Account changed');
          const { data } = await ConversationApi.getPersonalRead({
            id,
            explicitAll: true,
            throughId,
          });
          if (!contextMatches(rootGetters, identity))
            throw new Error('Account changed');
          if (!data.personal_read_enabled) {
            const { data: legacy } = await ConversationApi.markMessageRead({
              id,
            });
            if (contextMatches(rootGetters, identity))
              apply(commit, legacy, identity);
            return legacy;
          }
          apply(commit, data, identity);
          if (
            generation !== undefined &&
            generation !== data.personal_read_generation
          )
            throw new Error('Read state changed; try again');
          generation = data.personal_read_generation;
          throughId ??= data.personal_read_through_id;
          const observedIds = (data.personal_observed_message_ids || []).join(
            ',',
          );
          if (!data.unread_count || !observedIds) return data;
          if (!data.personal_read_token || observedIds === previousIds)
            throw new Error('Read progress changed; try again');
          previousIds = observedIds;
          const { data: result } = await ConversationApi.markMessageRead({
            id,
            readToken: data.personal_read_token,
          });
          if (!contextMatches(rootGetters, identity))
            throw new Error('Account changed');
          apply(commit, result, identity);
          if (result.personal_read_generation !== data.personal_read_generation)
            throw new Error('Read state changed; try again');
          if (!result.unread_count) return result;
        }
        throw new Error('Read batch limit reached');
      }).finally(() => {
        if (contextMatches(rootGetters, identity)) {
          dispatch('refreshPersonalReadState', { id });
          dispatch('conversationUnreadCounts/get', {}, { root: true });
        }
      });
    }
    try {
      const { data } = await ConversationApi.markMessageRead({ id });
      if (contextMatches(rootGetters, identity)) apply(commit, data, identity);
      return data;
    } catch (error) {
      if (explicit) throw error;
      return undefined;
    }
  },

  async markMessagesUnread({ commit, rootGetters, dispatch }, { id }) {
    const identity = userContext(rootGetters);
    try {
      const { data } = await ConversationApi.markMessagesUnread({ id });
      if (contextMatches(rootGetters, identity)) {
        apply(commit, { id, ...data }, identity);
        dispatch('conversationUnreadCounts/get', {}, { root: true });
      }
    } catch (error) {
      throwErrorMessage(error);
    }
  },
};
